import { Button, ButtonType } from '@soteria/design-system-ve';
import { useCallback, useId, useRef } from 'react';

import { colorPickerGrid, colorPickerLabel, colorPickerSection, colorSwatch, resetButtonWrapper, selectedCheckmark } from './ColorPicker.css';

export interface ColorPickerProps {
  palette: readonly string[];
  selectedColorIndex: number | null;
  onColorSelect: (index: number | null) => void;
}

/**
 * Renders the avatar color picker with roving tabIndex and arrow-key navigation
 * following the WAI-ARIA Radio Group pattern.
 */
export function ColorPicker({ palette, selectedColorIndex, onColorSelect }: ColorPickerProps) {
  const swatchRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const labelId = useId();

  const activeIndex = selectedColorIndex ?? 0;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { key } = event;
      let nextIndex: number | undefined;

      switch (key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (activeIndex + 1) % palette.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (activeIndex - 1 + palette.length) % palette.length;
          break;
        default:
          return;
      }

      event.preventDefault();
      onColorSelect(nextIndex);
      swatchRefs.current[nextIndex]?.focus();
    },
    [activeIndex, palette.length, onColorSelect]
  );

  return (
    <div className={colorPickerSection}>
      <div className={colorPickerLabel} id={labelId}>
        Avatar Color
      </div>
      <div aria-labelledby={labelId} className={colorPickerGrid} role="radiogroup" tabIndex={-1} onKeyDown={handleKeyDown}>
        {palette.map((color, index) => (
          <button
            key={color}
            ref={el => {
              swatchRefs.current[index] = el;
            }}
            aria-checked={selectedColorIndex === index}
            aria-label={`Color ${index + 1}`}
            className={colorSwatch}
            data-selected={selectedColorIndex === index}
            role="radio"
            style={{ backgroundColor: color }}
            tabIndex={index === activeIndex ? 0 : -1}
            type="button"
            onClick={() => onColorSelect(index)}
          >
            {selectedColorIndex === index && (
              <svg
                aria-hidden="true"
                className={selectedCheckmark}
                fill="currentColor"
                height="18"
                viewBox="0 0 24 24"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
            )}
          </button>
        ))}
      </div>
      {selectedColorIndex !== null && (
        <div className={resetButtonWrapper}>
          <Button buttonType={ButtonType.Tertiary} label="Reset to Default" onClick={() => onColorSelect(null)} />
        </div>
      )}
    </div>
  );
}
